import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useRouter } from "next/router";
import { createStructuredSelector } from "reselect";
import { useFormattedMessage } from "hooks";
import SideModal from "components/SideModal";
import InvestmentSum from "./InvestmentSum";
import InvestmentStartScreen from "./InvestmentStartScreen";
import InvestmentProcess from "./InvestmentProcess";
import { RenderContinueData } from "components/InvestmentContinueModal";
import { HIDE_ADEQUACY_TILE_STATUS_STEP, USER_ROLES, RESPONSE_ERRORS, TENANTS_LIST } from "constants/index";
import { cancelInvestment } from "api";
import { setAdequacyStart } from "store/user/adequacy/actions";
import {
    fetchUserInvestmentsStart,
    clearInvestmentStepData,
    setInvestmentStepData,
    setInitialInvestMentDataStart,
    resetInvestmentStepData,
} from "store/user/investment/actions";
import {
    selectContinueInvestments,
    selectFullInvestmentDetails,
    selectInvestmentStepData,
    selectIsInvestmentFailed,
} from "store/user/investment/selectors";
import { selectUserDashboardDetails, selectEmail, selectUserRoles } from "store/user/account/selectors";
import { selectProjectDetails, selectProjectType1to8M } from "store/project/selectors";
import { selectCurrenKYCStatus } from "store/user/kyc/selectors";
import { fetchKYCStart } from "store/user/kyc/actions";
import { KYC_STATUS_LOCAL } from "constants/status";
import { checkGTM } from "utils";
import { selectTenant } from "store/app/selectors";

const cookiesPkg = require("js-cookie");
const FINX_COOKIE = require("common/constants");

const messages = {
    heading: "investment_process",
};

export const RenderInvestmentStep = ({
    project,
    step,
    onCloseButtonClick,
    kycCompleted,
    adequacyCompleted,
    setInvestmentStepData,
    numberOfShares,
    setInitialInvestMentDataStart,
    comingFrom,
    projectID,
    setInvestmentKycModalFlag,
    hideInvestmentModal,
    userEmail,
    continueInvestmentData,
    fetchUserInvestmentsStart,
    VIPUser,
    isInitInvestmentLoading,
    setIsInitInvestmentSuccess,
    openInvestment,
    open,
    backSlideEffect = "",
    setBackSlideEffect,
    minInvestShare,
    maxInvestAmount,
    dividableBy,
    backOnInvestment,
    setBackOnInvestment,
    isGfkOnboardingProduct,
    isEwPgInvestmentProcess,
    productCatalog,
    productType,
    currentTenant,
}) => {
    const [investmentProcessBack, setInvestmentProcessBack] = useState(false);
    const [isCancelLoading, setCancelLoading] = useState(false);
    const [comeFrom, setComeFrom] = useState("");

    useEffect(() => {
        if (backOnInvestment) {
            if (step === "investmentSum") {
                onCloseButtonClick();
                setBackOnInvestment();
            } else if (step === "investmentStartScreen") {
                setBackOnInvestment();
                setInvestmentStepData({ step: "investmentSum" });
            } else {
                setInvestmentProcessBack(true);
            }
        }
    }, [backOnInvestment]);

    function renderInvestmentStep() {
        switch (step) {
            case "investmentSum":
                return (
                    <InvestmentSum
                        isEwPgInvestmentProcess={isEwPgInvestmentProcess}
                        open={open}
                        projectID={projectID}
                        minInvestShare={minInvestShare}
                        maxInvestAmount={maxInvestAmount}
                        dividableBy={dividableBy}
                        backSlideEffect={backSlideEffect}
                        setBackSlideEffect={setBackSlideEffect}
                        isInitInvestmentLoading={isInitInvestmentLoading}
                        numberOfShares={numberOfShares}
                        continueShares={continueInvestmentData.Shares || ""}
                        comeFrom={comeFrom}
                        typeInvestmentProduct={project?.typeInvestmentProduct}
                        onCloseButtonClick={onCloseButtonClick}
                        onSubmit={(values) => {
                            if (currentTenant === TENANTS_LIST.SACHWERTINVEST) {
                                checkGTM();
                                window?.dataLayer?.push({ event: "swi_start_invest_proces" });
                            }

                            const additionalParams = values.isMoreThan1k
                                ? {
                                      SelfDeclarationMoreThan100000FreelyDisposable: `${values.moreThan100kFreelyDisposable}`,
                                      SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${values.notMoreThanDoubleMonthlyIncome}`,
                                  }
                                : values.isMoreThan10k
                                  ? {
                                        SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${values.notMoreThanDoubleMonthlyIncome}`,
                                    }
                                  : {};

                            if (kycCompleted && projectID) {
                                //CALL IT FIRST
                                setInvestmentStepData &&
                                    setInvestmentStepData({
                                        investSum: {
                                            digitalShares: values.digitalShares,
                                            sum: values.digitalShares * 1,
                                            projectID,
                                            ID: continueInvestmentData.uid || "",
                                        },
                                        invest1to8MData: {
                                            ...additionalParams,
                                            isMoreThan1k: values.isMoreThan1k,
                                            isMoreThan10k: values.isMoreThan10k,
                                        },
                                        ...comingFrom,
                                    });

                                setInitialInvestMentDataStart &&
                                    setInitialInvestMentDataStart({
                                        investment: {
                                            ProjectID: projectID,
                                            Amount: `${values.digitalShares * 1}`,
                                            UserEmail: userEmail,
                                            uid: continueInvestmentData.uid || "",
                                            ...additionalParams,
                                        },
                                    });
                            } else {
                                setBackSlideEffect("step-slide-left");
                                setInvestmentStepData &&
                                    setInvestmentStepData({
                                        step: "investmentStartScreen",
                                        investSum: {
                                            digitalShares: values.digitalShares,
                                            sum: values.digitalShares * 1,
                                            projectID,
                                            ID: continueInvestmentData.uid || "",
                                        },
                                        invest1to8MData: additionalParams,
                                        ...comingFrom,
                                    });
                            }
                        }}
                    />
                );
            case "investmentStartScreen":
                return (
                    <InvestmentStartScreen
                        backSlideEffect={backSlideEffect}
                        setBackSlideEffect={setBackSlideEffect}
                        setIsInitInvestmentSuccess={setIsInitInvestmentSuccess}
                    />
                );
            case "investmentProcess":
                return (
                    <InvestmentProcess
                        backSlideEffect={backSlideEffect}
                        setBackSlideEffect={setBackSlideEffect}
                        investmentProcessBack={investmentProcessBack}
                        setInvestmentProcessBack={() => {
                            setInvestmentProcessBack(false);
                            setBackOnInvestment();
                        }}
                        userEmail={userEmail}
                        kycCompleted={kycCompleted}
                        adequacyCompleted={adequacyCompleted}
                        onCloseButtonClick={onCloseButtonClick}
                        setInvestmentKycModalFlag={setInvestmentKycModalFlag}
                        hideInvestmentModal={hideInvestmentModal}
                        openInvestment={openInvestment}
                        open={open}
                        isGfkOnboardingProduct={isGfkOnboardingProduct}
                        isEwPgInvestmentProcess={isEwPgInvestmentProcess}
                        productCatalog={productCatalog}
                        productType={productType}
                    />
                );
            case "renderContinueData":
                return (
                    <RenderContinueData
                        backSlideEffect={backSlideEffect}
                        withInvestment
                        project={project}
                        VIPUser={VIPUser}
                        isCancelLoading={isCancelLoading}
                        investmentData={continueInvestmentData}
                        onContinue={() => {
                            setBackSlideEffect("step-slide-left");
                            setComeFrom("continue");
                            setInvestmentStepData &&
                                setInvestmentStepData({
                                    step: "investmentSum",
                                    withContinueStep: true,
                                });
                        }}
                        onCancel={async () => {
                            try {
                                setCancelLoading(true);
                                const res = await cancelInvestment(continueInvestmentData && continueInvestmentData.uid);
                                if (res && res.data && !res.data.errors) {
                                    setBackSlideEffect("step-slide-right");
                                    setComeFrom("cancel");
                                    fetchUserInvestmentsStart({ projectID, email: userEmail });
                                    setCancelLoading(false);
                                    setInvestmentStepData &&
                                        setInvestmentStepData({
                                            step: "investmentSum",
                                            withContinueStep: true,
                                        });
                                }
                            } catch (error) {
                                setCancelLoading(false);
                            }
                        }}
                    />
                );
            default:
                return <InvestmentSum />;
        }
    }
    return renderInvestmentStep();
};

const InvestmentModal = ({
    open,
    onCloseButtonClick,
    userDashboardDetails,
    setInitialInvestMentDataStart,
    numberOfShares,
    setInvestmentStepData,
    setAdequacyStart,
    projectData = [],
    setInvestmentKycModalFlag,
    hideInvestmentModal,
    userEmail,
    clearInvestmentStepData,
    fetchUserInvestmentsStart,
    continueInvestmentData = {},
    investmentStepData,
    fullInvestmentDetails,
    openInvestment,
    userRoles = [],
    minInvestShare,
    maxInvestAmount,
    dividableBy,
    createInvestmentError,
    isInvestment1to8M,
    resetInvestmentStepData,
    backOnInvestment,
    setBackOnInvestment,
    isGfkOnboardingProduct,
    isEwPgInvestmentProcess,
    fetchKycStart,
    currentKYCStatus,
    productCatalog,
    productType,
    currentTenant,
}) => {
    const inheritUserToken = cookiesPkg.get(FINX_COOKIE.INHERIT_USER_TOKEN);
    const inheritUserEmail = cookiesPkg.get(FINX_COOKIE.INHERIT_USER_EMAIL);
    const isInheritUserEnabled = !!(inheritUserToken && inheritUserEmail);

    const router = useRouter();
    const { formatMessage } = useFormattedMessage();

    const isUserInvestmentsLoading = fullInvestmentDetails.isUserInvestmentsLoading,
        isInitInvestmentLoading = fullInvestmentDetails.isInitInvestmentLoading;
    // isInitInvestmentSuccess = fullInvestmentDetails.isInitInvestmentSuccess;

    const [backSlideEffect, setBackSlideEffect] = useState("");

    const [isInitInvestmentSuccess, setIsInitInvestmentSuccess] = useState(fullInvestmentDetails.isInitInvestmentSuccess);

    const step = investmentStepData.step,
        withContinueStep = investmentStepData.withContinueStep,
        clearData = investmentStepData.clearData,
        comingFrom = investmentStepData.investmentData.userData.comingFrom || {};

    const StatusAdequacy = userDashboardDetails.StatusAdequacy;
    const fetchedProjects = projectData;
    let project = null;
    if (router.query && router.query.id && fetchedProjects && Array.isArray(fetchedProjects) && fetchedProjects.length) {
        project = fetchedProjects.find((item) => item.projectID === router.query.id);
    }

    const projectID = project && project.projectID;
    const hasKycStarted =
        currentKYCStatus == KYC_STATUS_LOCAL.COMPLETED ||
        currentKYCStatus == KYC_STATUS_LOCAL.INITIATED ||
        currentKYCStatus == KYC_STATUS_LOCAL.PENDING ||
        currentKYCStatus == KYC_STATUS_LOCAL.REKYC_PENDING ||
        currentKYCStatus == KYC_STATUS_LOCAL.REKYC_INITIATED ||
        currentKYCStatus == KYC_STATUS_LOCAL.REKYC;

    const isAdequacyCompleted = !!HIDE_ADEQUACY_TILE_STATUS_STEP.find((status) => status === StatusAdequacy);
    const VIPUser = userRoles && userRoles.length && !!userRoles.find((role) => role === USER_ROLES.VIP);
    const [renderStep, setRenderStep] = useState(step);

    useEffect(() => {
        if (!isAdequacyCompleted && !hasKycStarted) {
            setInvestmentStepData({ investmentData: { step: "investmentUserDataStep" } });
        }
        !isAdequacyCompleted && setAdequacyStart({ step: 1 });
    }, []);

    useEffect(() => {
        projectID && userEmail && fetchUserInvestmentsStart({ projectID, email: userEmail });
    }, [projectID, userEmail]);

    useEffect(() => {
        open && !isInheritUserEnabled && fetchKycStart();
    }, [open, isInheritUserEnabled]);

    useEffect(() => {
        if (isInvestment1to8M && createInvestmentError == RESPONSE_ERRORS.SELF_DECLARATION_ERROR && step !== 1) {
            resetInvestmentStepData();
            setRenderStep("investmentSum");
        }
    }, [createInvestmentError, isInvestment1to8M, step]);

    useEffect(() => {
        setIsInitInvestmentSuccess(fullInvestmentDetails.isInitInvestmentSuccess);
    }, [fullInvestmentDetails.isInitInvestmentSuccess]);

    useEffect(() => {
        if (clearData === true && onCloseButtonClick) {
            onCloseButtonClick();
        }
    }, [clearData]);

    // Render continue investment step
    useEffect(() => {
        setRenderStep(
            !openInvestment &&
                step !== "investmentStartScreen" &&
                step !== "investmentProcess" &&
                !withContinueStep &&
                Object.keys(continueInvestmentData).length > 0
                ? "renderContinueData"
                : step,
        );
    }, [step, withContinueStep, continueInvestmentData, openInvestment]);

    useEffect(() => {
        if (hasKycStarted === true) {
            setInvestmentStepData({ investmentData: { step: "renderAdequacyStep" } });
            step !== "investmentSum" && step !== "renderContinueData" && setBackSlideEffect("step-slide-left");
        }
    }, [hasKycStarted, step]);

    useEffect(() => {
        let data;
        if (step === "investmentSum" || step === "investmentProcess") {
            data =
                step === "investmentSum" && hasKycStarted === true
                    ? { step: "investmentStartScreen", investmentData: { step: "renderAdequacyStep" } }
                    : { step: "investmentStartScreen" };
            data =
                step === "investmentProcess" && hasKycStarted === true
                    ? { step: "investmentProcess", investmentData: { step: "renderAdequacyStep" } }
                    : { step: "investmentStartScreen" };
            if (isInitInvestmentSuccess) {
                setInvestmentStepData(data);
                setBackSlideEffect("step-slide-left");
            }
        }
    }, [isInitInvestmentSuccess, step]);

    const memoizedRenderInvestmentStep = useMemo(() => {
        return (
            <RenderInvestmentStep
                project={project}
                setBackOnInvestment={setBackOnInvestment}
                backOnInvestment={backOnInvestment}
                numberOfShares={numberOfShares}
                step={renderStep}
                kycCompleted={hasKycStarted}
                onCloseButtonClick={onCloseButtonClick}
                adequacyCompleted={isAdequacyCompleted}
                comingFrom={comingFrom}
                setInitialInvestMentDataStart={setInitialInvestMentDataStart}
                setInvestmentStepData={setInvestmentStepData}
                projectID={projectID}
                setInvestmentKycModalFlag={setInvestmentKycModalFlag}
                hideInvestmentModal={hideInvestmentModal}
                userEmail={userEmail}
                continueInvestmentData={continueInvestmentData}
                fetchUserInvestmentsStart={fetchUserInvestmentsStart}
                VIPUser={VIPUser}
                isInitInvestmentLoading={isInitInvestmentLoading}
                isInitInvestmentSuccess={isInitInvestmentSuccess}
                setIsInitInvestmentSuccess={setIsInitInvestmentSuccess}
                openInvestment={openInvestment}
                open={open}
                backSlideEffect={backSlideEffect}
                setBackSlideEffect={setBackSlideEffect}
                minInvestShare={minInvestShare}
                maxInvestAmount={maxInvestAmount}
                dividableBy={dividableBy}
                isGfkOnboardingProduct={isGfkOnboardingProduct}
                isEwPgInvestmentProcess={isEwPgInvestmentProcess}
                productCatalog={productCatalog}
                productType={productType}
                currentTenant={currentTenant}
            />
        );
    }, [
        isAdequacyCompleted,
        step,
        hasKycStarted,
        userEmail,
        backOnInvestment,
        numberOfShares,
        withContinueStep,
        renderStep,
        isUserInvestmentsLoading,
        isInitInvestmentLoading,
        isInitInvestmentSuccess,
        open,
        backSlideEffect,
    ]);

    return (
        <SideModal
            className="investment-modal"
            wrapClassName="investment-modal-wrap"
            heading={formatMessage(messages.heading)}
            open={open}
            onCloseButtonClick={() => {
                onCloseButtonClick();
                clearInvestmentStepData();
                isAdequacyCompleted && setAdequacyStart({ step: 1 });
            }}
        >
            {memoizedRenderInvestmentStep}
        </SideModal>
    );
};

const mapStateToProps = createStructuredSelector({
    userRoles: selectUserRoles,
    continueInvestmentData: selectContinueInvestments,
    fullInvestmentDetails: selectFullInvestmentDetails,
    investmentStepData: selectInvestmentStepData,
    userDashboardDetails: selectUserDashboardDetails,
    projectData: selectProjectDetails,
    userEmail: selectEmail,
    createInvestmentError: selectIsInvestmentFailed,
    isInvestment1to8M: selectProjectType1to8M,
    currentKYCStatus: selectCurrenKYCStatus,
    currentTenant: selectTenant,
});

const mapDispatchToProps = (dispatch) => ({
    setInvestmentStepData: (data) => dispatch(setInvestmentStepData(data)),
    resetInvestmentStepData: (data) => dispatch(resetInvestmentStepData(data)),
    setInitialInvestMentDataStart: (data) => dispatch(setInitialInvestMentDataStart(data)),
    fetchUserInvestmentsStart: (data) => dispatch(fetchUserInvestmentsStart(data)),
    clearInvestmentStepData: () => dispatch(clearInvestmentStepData()),
    setAdequacyStart: (data) => dispatch(setAdequacyStart(data)),
    fetchKycStart: () => dispatch(fetchKYCStart()),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvestmentModal);
